import { useToken } from '@ori/auth';
import { CUSTOMER_TYPE_ID, VISITOR_SEGMENT, getHeaders, useGraphqlQuery } from '@ori/fetching';
import { VisitorSegment } from '@ori/graphql-schema';
import { useCookieStoreInfo } from '@ori/personal-beauty-store';
import { useUserSegmentationData } from '@ori/presentation-http';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { APPLICATION_NAME } from '../../constants';
import type {
  BenefitBannerFragment,
  GetBenefitBannerQuery,
  GetBenefitBannerQueryVariables,
  VipAccessResponse,
} from '../../models';
import { benefitBannerQuery } from '../../queries';
import { useAppConfig } from '../configurationContext';
import { useConsultant } from '../consultantContext';
import { isLoading, isOrderOnBehalfVisible, shouldSkipBenefitBannerFetch } from './utils';

export interface TopAreaBannerContext {
  benefitBannerData: BenefitBannerFragment | undefined;
  isAnonymousWithStore: boolean;
  isOrderOnBehalfVisible: boolean;
  loading: boolean;
  orderOnBehalfData: VipAccessResponse | undefined;
  visible: boolean;
}

const defaultTopAreaBannerContextValue: TopAreaBannerContext = {
  benefitBannerData: undefined,
  isAnonymousWithStore: false,
  isOrderOnBehalfVisible: false,
  loading: true,
  orderOnBehalfData: undefined,
  visible: false,
};

export const topAreaBannerContext = createContext<TopAreaBannerContext>(defaultTopAreaBannerContextValue);

const { Provider } = topAreaBannerContext;

export const TopAreaBannerProvider: FC<PropsWithChildren> = ({ children }) => {
  const { owner: store } = useCookieStoreInfo();
  const { loadingVipAccess, vipAccess: orderOnBehalfData } = useConsultant();
  const { enableTopAreaBanner, currentLanguage, graphqlUrl, tenant } = useAppConfig();
  const { customerId, isAnonymousUser, token } = useToken();
  const { customerTypeId, visitorSegment, loading: loadingSegmentationData } = useUserSegmentationData();
  const isAnonymousWithStore = useMemo(() => isAnonymousUser && !!store, [isAnonymousUser, store]);
  /**
   * VipAccessData can be defined but can arrive with empty key properties.
   */
  const orderOnBehalfVisible = useMemo(() => isOrderOnBehalfVisible(orderOnBehalfData), [orderOnBehalfData]);
  const headers = useMemo(
    () => ({
      ...getHeaders({
        customerId,
        language: currentLanguage,
        sourceAppName: APPLICATION_NAME,
        tenant,
        token,
      }),
      [CUSTOMER_TYPE_ID]: customerTypeId ?? 0,
      [VISITOR_SEGMENT]: visitorSegment ?? VisitorSegment.Anonymous,
    }),
    [currentLanguage, customerId, customerTypeId, tenant, token, visitorSegment],
  );
  const { data: benefitBannerData, error } = useGraphqlQuery<GetBenefitBannerQuery, GetBenefitBannerQueryVariables>({
    query: benefitBannerQuery,
    url: graphqlUrl,
    headers,
    skip: shouldSkipBenefitBannerFetch({
      enableTopAreaBanner,
      isAnonymousWithStore,
      loadingSegmentationData,
      orderOnBehalfVisible,
    }),
    swrConfig: {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  });
  const visible = orderOnBehalfVisible || Boolean(enableTopAreaBanner);
  const { messageTitle, messageUrl } = benefitBannerData?.application?.frontpage?.benefitBanner ?? {};

  return (
    <Provider
      value={useMemo<TopAreaBannerContext>(
        () => ({
          benefitBannerData: { messageTitle, messageUrl },
          isAnonymousWithStore,
          isOrderOnBehalfVisible: orderOnBehalfVisible,
          loading: isLoading({
            benefitBannerData,
            error,
            isAnonymousWithStore,
            loadingVipAccess,
            orderOnBehalfVisible,
          }),
          orderOnBehalfData,
          visible,
        }),
        [
          benefitBannerData,
          error,
          isAnonymousWithStore,
          loadingVipAccess,
          messageTitle,
          messageUrl,
          orderOnBehalfData,
          orderOnBehalfVisible,
          visible,
        ],
      )}
    >
      {children}
    </Provider>
  );
};

export const useTopAreaBanner = () => useContext(topAreaBannerContext);
